import qs from 'qs';
import Client, { Endpoint } from './client';
import * as AnalyticsCookie from './analytics-cookie';
import { Session } from './session';
import * as events from '../schemas/analytics';
import { trackPageViewForLocation, trackGenericPageView } from './mparticle_tracker';

interface Gauge {
  name: string;
  value: number;
  tags?: string[];
  sample?: number;
}

interface SentryErrorReporter {
  captureException(Error, object?): void;
  configureScope(cb: (scope) => void);
}

interface Config {
  host: string;
  timeout?: number;
  apiKey: string;
  buffer?: number;
  local?: boolean;
  enabled?: boolean;
  sentryClient?: SentryErrorReporter;
  domain?: string;
}

const ERROR = 'error';
const INFO = 'info';
let ENABLED = true;

let sentryClient: SentryErrorReporter = null;

const userContext: Partial<events.UserContext> = {
  cookie_id: AnalyticsCookie.trackingID(),
};

const session = new Session(userContext);

function buildEvent(name, data = {}, jsError?, level?) {
  let eventName = name;
  let props;

  if (name.$type) {
    eventName = name.$type.name;
    props = name;
  } else {
    props = data;
  }

  const eventData = { ...props };
  delete eventData.$type;

  // Remove experiments from normal elog tracking as they are no longer used
  // The user_context itself is shared in a few places, so making this change
  // here and not in the indentify call will restore this functionality
  //
  // https://github.com/reverbdotcom/frontend/pull/5100/files
  eventData.user_context = { ...userContext, experiments: [] };

  return {
    level,
    name: eventName,
    data: eventData,
    error: jsError,
  };
}

export function configure(opts: Config) {
  if (Object.hasOwnProperty.call(opts, 'enabled')) {
    ENABLED = opts.enabled;
  }

  if (opts.sentryClient) {
    sentryClient = opts.sentryClient;
  }

  Client.configure({
    host: opts.host,
    timeout: opts.timeout,
    apiKey: opts.apiKey,
    buffer: opts.buffer,
    local: opts.local,
  });

  AnalyticsCookie.configure(opts);

  // Refresh tracking ID since we may not have been able to persist
  // the cookie on the default domain
  userContext.cookie_id = AnalyticsCookie.trackingID();
}

export function identify(user) {
  if (sentryClient) {
    sentryClient.configureScope((scope) => {
      const { id, email } = user;

      scope.setUser({
        id,
        email,
      });
    });
  }

  const normalizedUserAttributes = { ...user, experiments: transformExperiments(user) };
  Object.assign(userContext, normalizedUserAttributes);
}

function transformExperiments(user) {
  if (!user.experiments) return [];

  return user.experiments.map((exp) => {
    return (({ name, value }) => ({ name, value }))(exp);
  });
}

export function post(event, endpoint: Endpoint) {
  if (ENABLED !== true) { return; }
  Client.post([event], endpoint);
}

export function track(name, data = {}) {
  const event = buildEvent(name, data);
  post(event, 'events');
}

export type AnalyticsQueryParams = {
  utm_source?: string;
  utm_campaign?: string;
  utm_medium?: string;
  gs_partner?: string;
};

export function pageView(loc: Partial<Location> = window.location, doc: Partial<Document> = document) {
  const q = qs.parse(loc.search, { ignoreQueryPrefix: true }) as AnalyticsQueryParams;
  session.touch(loc, doc);

  const pageView = new events.PageView({
    referer: session.referer(),
    url: session.url(),
    id: session.pageViewID(),
    utm_source: q.utm_source,
    utm_campaign: q.utm_campaign,
    utm_medium: q.utm_medium,
  });

  track(pageView);
  trackGenericPageView();
  trackPageViewForLocation(loc, userContext);
}

function componentTrack(data: events.ComponentsView | events.ComponentInteraction, loc) {
  const q = qs.parse(loc.search, { ignoreQueryPrefix: true });

  track({
    page_view_id: session.pageViewID(),
    url: session.url(),
    referer: session.referer(),
    utm_source: q.utm_source,
    utm_campaign: q.utm_campaign,
    utm_medium: q.utm_medium,
    $type: data.$type,
    ...data,
  });
}

export function currentSession(): Session {
  return session;
}

export function view(data: events.ComponentsView, loc = window.location) {
  return componentTrack(data, loc);
}

export function interaction(data: events.ComponentInteraction, loc = window.location) {
  return componentTrack(data, loc);
}

export function error(name: string, data = {}, jsError: string | Error = null, report = false) {
  const event = buildEvent(name, data, jsError, ERROR);

  if (!jsError) {
    // eslint-disable-next-line no-param-reassign
    jsError = name;
  }

  if (!(jsError as Error).message) {
    // If we just have a string instead of an error, wrap in an error so we get a stack trace
    // eslint-disable-next-line no-param-reassign
    jsError = new Error(jsError as string);
  }

  if (sentryClient && report) {
    sentryClient.configureScope((scope) => {
      scope.setFingerprint(['{{ default }}', name]);
      scope.setExtras(data);
      scope.setTags({
        session_id: session.ID(),
        request_id: session.requestId(),
        context_id: session.cookieId(),
      });

      sentryClient.captureException(jsError);
    });
  }

  // ensure that errors are reported to the console
  console.error(jsError);
  post(event, 'events');
}

export function info(name: string, data: Record<string, any> = {}) {
  const event = buildEvent(name, data, null, INFO);
  post(event, 'events');
}

export function gauge(gauge: Gauge) {
  post(gauge, 'gauges');
}

export const elog = {
  info,
  error,
};

export { events };
